/**
 * 拖檔案到某個區塊的共用邏輯（`RequestsView` 代客戶建立草稿卡、`ResellerRequestForm` 共用）。
 *
 * ## 為什麼不是各寫三行 `@dragover.prevent` 就好（2026-09-17 主人回報「無法拖曳上傳」）
 *
 * 1. **`dragenter` 一定要 `preventDefault`。** HTML 拖放規範：滑鼠移到新元素時先發 `dragenter`，
 *    沒被取消的話，非文字輸入、非 `<body>` 的元素**不會**成為 drop target，瀏覽器會把目標改指到
 *    `<body>`——之後 `dragover`／`drop` 都發到 body，區塊上的 handler 根本收不到。Chromium 寬鬆
 *    （只看 `dragover` 有沒有取消）所以本機開發看不出來；規範嚴格的瀏覽器就是「拖上去沒反應」。
 *    原本兩份 dropzone 都只攔 `dragover`／`dragleave`／`drop`，缺這一個。
 * 2. **抓不到檔案時不能靜默。** 從 Outlook／LINE／Teams 這類軟體直接把附件拖進瀏覽器，
 *    `dataTransfer.files` 是空的（那些程式給的是它們自家的剪貼格式，瀏覧器拿不到內容），
 *    舊碼 `if (f) …` 一路不進去、畫面一點反應都沒有——而這張卡的情境正是「客戶把
 *    request.json **email** 給我們」。所以：`files` 空就退到 `items[].getAsFile()`；還是沒有
 *    就走 `onNoFile`，呼叫端**一定要**跳提示（存到電腦再拖、或點擊選檔、或貼上內容）。
 * 3. `dragleave` 在滑過子元素（`<p>`／`<strong>`）時會先發，只用布林會讓高亮閃爍；
 *    用 enter/leave 計數，歸零才算離開。
 */
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

export interface FileDropOptions {
  /** 拿到檔案（`files[0]`，或 `items` 裡第一個 kind=file）。 */
  onFile: (file: File) => void | Promise<void>
  /** 拖進來了但一個檔案都抓不到——呼叫端要給使用者看得見的回饋，不可省。 */
  onNoFile?: (e: DragEvent) => void
}

export interface FileDropHandlers {
  dragenter: (e: DragEvent) => void
  dragover: (e: DragEvent) => void
  dragleave: (e: DragEvent) => void
  drop: (e: DragEvent) => void
}

/** 從一次 drop 的 `dataTransfer` 挑出第一個檔案；沒有回 `null`（不擲錯）。 */
export function extractDroppedFile(dt: DataTransfer | null | undefined): File | null {
  if (!dt) return null
  const direct = dt.files && dt.files.length > 0 ? dt.files[0] : null
  if (direct) return direct
  const items = dt.items ? Array.from(dt.items) : []
  for (const item of items) {
    if (item.kind === 'file') {
      const f = item.getAsFile()
      if (f) return f
    }
  }
  return null
}

/**
 * 用法（模板）：`<div class="drop" :class="{ over }" v-on="handlers" @click="…">`
 * `over` 是頂層 ref，模板自動解包；`handlers` 用 `v-on` 物件綁定四個事件。
 */
export function useFileDrop(opts: FileDropOptions): { over: Ref<boolean>; handlers: FileDropHandlers } {
  const over = ref(false)
  let depth = 0
  const handlers: FileDropHandlers = {
    dragenter(e) {
      e.preventDefault()
      depth += 1
      over.value = true
    },
    dragover(e) {
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
      over.value = true
    },
    dragleave(e) {
      e.preventDefault()
      depth = Math.max(0, depth - 1)
      if (depth === 0) over.value = false
    },
    drop(e) {
      e.preventDefault()
      depth = 0
      over.value = false
      const f = extractDroppedFile(e.dataTransfer)
      if (f) void opts.onFile(f)
      else opts.onNoFile?.(e)
    },
  }
  return { over, handlers }
}

/** 這次拖曳帶的是不是**檔案**（OS 檔案拖曳的 `types` 會有 `"Files"`；拖文字／連結沒有）。 */
export function dragHasFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types
  return !!types && Array.from(types).includes('Files')
}

/**
 * 把接收面放寬到**整個視窗**（主人 2026-09-17 回報從檔案總管拖進虛線框還是沒反應）：
 * 只要 `active()` 為真，檔案拖到頁面任何位置都交給同一組 handlers——不必精準對到虛線框、
 * 放錯位置也不會變成瀏覽器直接開那個 JSON 檔（`dragover` 一律 preventDefault）。
 *
 * - 只處理**檔案**拖曳（`dragHasFiles`），拖選字、拖連結不動。
 * - 元素上的 handler 先跑過的事件（`defaultPrevented`）這裡跳過，避免同一次 drop 讀兩次。
 * - 離開視窗（`dragleave` 的 `relatedTarget === null`）才算真的離開，滑過子元素不算。
 */
export function useWindowFileDrop(active: () => boolean, handlers: FileDropHandlers): void {
  const guard = (fn: (e: DragEvent) => void) => (e: DragEvent) => {
    if (!active() || e.defaultPrevented || !dragHasFiles(e)) return
    fn(e)
  }
  const onEnter = guard((e) => handlers.dragenter(e))
  const onOver = guard((e) => handlers.dragover(e))
  const onLeave = (e: DragEvent) => {
    if (!active() || e.defaultPrevented) return
    if (e.relatedTarget === null) handlers.dragleave(e)
  }
  const onDrop = guard((e) => handlers.drop(e))
  onMounted(() => {
    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragover', onOver)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('dragenter', onEnter)
    window.removeEventListener('dragover', onOver)
    window.removeEventListener('dragleave', onLeave)
    window.removeEventListener('drop', onDrop)
  })
}

/** `onNoFile` 的標準文案（兩個 dropzone 同一句，別各寫一版）。 */
export const NO_FILE_DROPPED_MESSAGE =
  '拖進來的內容裡沒有可讀的檔案。從 Outlook、LINE 這類軟體直接拖附件時，瀏覽器拿不到檔案內容——' +
  '請先把 request.json 存到電腦再拖進來，或點擊此區選擇檔案，或把檔案內容貼到下方欄位。'
